import { API_URL } from '../../config';
import { listSkills } from '../gphSkillRegistry';

// Taxonomia completa da Mi Brain (espelha MI_SKILL_INTENTS do backend).
const INTENTS = new Set([
  'use_skill',
  'continue_pending',
  'ask_clarification',
  'vague_help',
  'small_talk',
  'out_of_scope',
  'noise_or_abuse',
  'general_help',
  'unsupported'
]);

const compactSkillContract = (skill = {}) => ({
  id: skill.id,
  name: skill.name,
  supportedGames: Array.isArray(skill.supportedGames) ? skill.supportedGames : [],
  description: skill.description || '',
  requiredSlots: Array.isArray(skill.requiredSlots) ? skill.requiredSlots : undefined,
  requiredSlotsByMode: skill.requiredSlotsByMode || undefined,
  requiredSlotsByIntent: skill.requiredSlotsByIntent || undefined,
  slotSchemas: skill.slotSchemas || {},
  askForMissingSlots: skill.askForMissingSlots || {},
  execution: {
    requiresBackend: Boolean(skill.execution?.requiresBackend),
    costClass: skill.execution?.costClass || 'low',
    requiresPlan: skill.execution?.requiresPlan || 'unknown'
  },
  guardrails: skill.guardrails || {},
  capabilityMapping: skill.capabilityMapping || {}
});

const normalizeExecutionPlan = (plan = []) => {
  if (!Array.isArray(plan)) return [];
  return plan.slice(0, 6).reduce((items, item) => {
    if (!item || typeof item !== 'object' || !item.skillId) return items;
    items.push({
      skillId: item.skillId,
      capabilityId: item.capabilityId || null,
      slots: item.slots && typeof item.slots === 'object' ? item.slots : {},
      missingSlots: Array.isArray(item.missingSlots) ? item.missingSlots : []
    });
    return items;
  }, []);
};

const normalizeResponsePlan = (responsePlan = null) => {
  if (!responsePlan || typeof responsePlan !== 'object') return null;
  return {
    format: responsePlan.format || 'text',
    groupBy: responsePlan.groupBy || 'none',
    summary: responsePlan.summary !== false
  };
};

const TASK_TYPES = new Set([
  'exact_calculation',
  'baseline_recommendation',
  'configuration_plan',
  'refinement',
  'previous_result_question',
  'diagnostic',
  'training',
  'analysis',
  'general_guidance',
  'unsupported'
]);

const ANSWER_STRATEGIES = new Set([
  'direct_answer',
  'ask_clarification',
  'run_skill',
  'run_skill_batch',
  'explain_limit_and_offer_baseline',
  'continue_previous',
  'switch_context'
]);

const normalizeDecision = (decision = {}) => {
  const intent = INTENTS.has(decision.intent) ? decision.intent : 'general_help';
  const taskType = TASK_TYPES.has(decision.taskType) ? decision.taskType : null;
  const answerStrategy = ANSWER_STRATEGIES.has(decision.answerStrategy) ? decision.answerStrategy : null;
  return {
    intent,
    skillId: decision.skillId || null,
    capabilityId: decision.capabilityId || null,
    confidence: Number.isFinite(Number(decision.confidence)) ? Number(decision.confidence) : 0,
    reason: decision.reason || '',
    taskType,
    requiresTool: decision.requiresTool === true,
    canAnswerWithGuidance: decision.canAnswerWithGuidance === true,
    answerStrategy,
    slots: decision.slots && typeof decision.slots === 'object' ? decision.slots : {},
    missingSlots: Array.isArray(decision.missingSlots) ? decision.missingSlots : [],
    executionPlan: normalizeExecutionPlan(decision.executionPlan),
    responsePlan: normalizeResponsePlan(decision.responsePlan),
    nextQuestion: decision.nextQuestion || null,
    userFacingMessage: decision.userFacingMessage || null,
    // Entitlement do backend (source of truth): bloqueio comercial da Mi.
    locked: decision.locked === true,
    lockType: decision.lockType || null,
    freeTrial: decision.freeTrial && typeof decision.freeTrial === 'object' ? decision.freeTrial : null,
    paywallSuggested: decision.paywallSuggested === true,
    source: decision.source || 'unknown'
  };
};

const compactSlots = (slots = {}) => Object.entries(slots || {}).reduce((acc, [key, value]) => {
  if (!value) {
    acc[key] = value;
    return acc;
  }
  if (key === 'video') {
    acc.video = value?.name
      ? { provided: true, name: value.name, type: value.type || null, size: value.size || null }
      : Boolean(value);
    return acc;
  }
  if (typeof File !== 'undefined' && typeof value === 'object' && value instanceof File) {
    acc[key] = { provided: true, name: value.name, type: value.type || null, size: value.size || null };
    return acc;
  }
  acc[key] = value;
  return acc;
}, {});

const compactLastSkillResult = (result = null) => {
  if (!result || typeof result !== 'object') return null;
  return {
    skillId: result.skillId || null,
    capabilityId: result.capabilityId || null,
    resultKind: result.resultKind || null,
    game: result.game || null,
    weapon: result.weapon || null,
    weaponLabel: result.weaponLabel || null,
    baseScope: result.baseScope || null,
    baseScopeLabel: result.baseScopeLabel || null,
    baseValue: result.baseValue ?? null,
    sensitivityType: result.sensitivityType || null,
    sensitivityTypeLabel: result.sensitivityTypeLabel || null,
    controlMode: result.controlMode || null,
    items: Array.isArray(result.items)
      ? result.items.slice(0, 6).map((item) => ({
        weapon: item.weapon || null,
        weaponLabel: item.weaponLabel || null,
        baseScope: item.baseScope || null,
        baseScopeLabel: item.baseScopeLabel || null,
        baseValue: item.baseValue ?? null,
        sensitivityType: item.sensitivityType || null,
        sensitivityTypeLabel: item.sensitivityTypeLabel || null
      }))
      : undefined,
    title: result.title || null,
    summary: result.summary || null
  };
};

const compactConversationContext = (context = {}) => ({
  pendingSkill: context.pendingSkill || null,
  pendingSlots: Array.isArray(context.pendingSlots) ? context.pendingSlots : [],
  slots: compactSlots(context.slots || {}),
  pendingExecutionPlan: context.pendingExecutionPlan && typeof context.pendingExecutionPlan === 'object'
    ? {
      skillId: context.pendingExecutionPlan.skillId || null,
      items: normalizeExecutionPlan(context.pendingExecutionPlan.items),
      responsePlan: normalizeResponsePlan(context.pendingExecutionPlan.responsePlan)
    }
    : null,
  lastAssistantQuestion: context.lastAssistantQuestion || null,
  lastUserText: context.lastUserText || null,
  lastAssistantText: context.lastAssistantText || null,
  lastSkillResult: compactLastSkillResult(context.lastSkillResult),
  lastCalculation: context.lastCalculation || null,
  lastDiagnosis: context.lastDiagnosis || null,
  lastTraining: context.lastTraining || null,
  lastAnalysis: context.lastAnalysis || null,
  playerMemory: context.playerMemory || {}
});

export const semanticResolverUnavailable = (reason = 'semantic_resolver_unavailable') => ({
  intent: 'general_help',
  skillId: null,
  capabilityId: null,
  confidence: 0,
  reason,
  taskType: null,
  requiresTool: false,
  canAnswerWithGuidance: false,
  answerStrategy: null,
  slots: {},
  missingSlots: [],
  executionPlan: [],
  responsePlan: null,
  nextQuestion: null,
  userFacingMessage: null,
  source: 'frontend_fallback'
});

export const resolveMiSkillIntentWithLLM = async ({
  text = '',
  conversationContext = {},
  pendingSkill = null,
  language = 'pt-BR',
  fetchImpl = globalThis.fetch,
  apiBaseUrl = API_URL,
  authToken = null,
  getAuthToken = null
} = {}) => {
  if (typeof fetchImpl !== 'function') {
    return semanticResolverUnavailable('fetch_unavailable');
  }

  try {
    const token = authToken || (typeof getAuthToken === 'function' ? await getAuthToken() : null);
    if (!token) return semanticResolverUnavailable('auth_token_unavailable');

    const skills = listSkills();
    const safeConversationContext = compactConversationContext(conversationContext);
    const response = await fetchImpl(`${apiBaseUrl}/mi/intent/resolve`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        text,
        language,
        conversationContext: safeConversationContext,
        pendingSkill: pendingSkill || safeConversationContext.pendingSkill || null,
        availableSkills: skills.map((skill) => skill.id),
        skillContracts: skills.map(compactSkillContract)
      })
    });

    if (!response.ok) {
      return semanticResolverUnavailable(`resolver_http_${response.status}`);
    }

    const data = await response.json().catch(() => null);
    return normalizeDecision(data || {});
  } catch (error) {
    return semanticResolverUnavailable('resolver_request_failed');
  }
};

export default resolveMiSkillIntentWithLLM;
